// Crea o edita una actividad de una práctica de conversación.

import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { IconButton } from '@/components/atoms/IconButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/molecules/Card';
import { useConversationActivities } from '@/hooks/useConversationData';
import { conversationBackofficeRoutes } from '@/constants/conversationBackofficeRoutes';
import type { ActivityFormData } from '../ActivityEditorPage/ActivityEditorPage.types';
import { ActivityForm } from '../ActivityEditorPage/components/ActivityForm';
import styles from './ConversationActivityFormPage.module.scss';

export function ConversationActivityFormPage() {
  const navigate = useNavigate();
  const { resourceId, activityId } = useParams<{ resourceId: string; activityId: string }>();
  const [sidebarContainer, setSidebarContainer] = React.useState<HTMLDivElement | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);

  const { activities, isLoading, saveActivity } = useConversationActivities(resourceId);

  const isCreateMode = !activityId || activityId === 'new';
  const activity = React.useMemo(() => {
    if (isCreateMode || !activityId) return undefined;
    return activities.find((a) => a.id === activityId);
  }, [activities, activityId, isCreateMode]);

  const handleBack = () => {
    const url = conversationBackofficeRoutes.edit(resourceId ?? '');
    navigate(`${url}?tab=activities`);
  };

  const handleSave = async (formData: ActivityFormData) => {
    const resolvedActivityId = isCreateMode ? null : (activityId ?? null);
    await saveActivity(resolvedActivityId, formData as Parameters<typeof saveActivity>[1]);
    handleBack();
  };

  const handleCancel = () => {
    handleBack();
  };

  if (!resourceId) {
    return (
      <div className={styles.resourceActivityFormPage}>
        <p className={styles.resourceActivityFormPage__error}>Conversation ID no encontrado</p>
      </div>
    );
  }

  if (isLoading && !isCreateMode) {
    return (
      <div className={styles.resourceActivityFormPage}>
        <div className={styles.resourceActivityFormPage__loading}>Cargando actividad...</div>
      </div>
    );
  }

  if (!isCreateMode && !activity) {
    return (
      <div className={styles.resourceActivityFormPage}>
        <p className={styles.resourceActivityFormPage__error}>Actividad no encontrada</p>
        <IconButton icon={ArrowLeft} onClick={handleBack} aria-label="Volver al listado" />
      </div>
    );
  }

  return (
    <div className={styles.resourceActivityFormPage}>
      <div className={styles.resourceActivityFormPage__toolbar}>
        <div className={styles.resourceActivityFormPage__header}>
          <IconButton icon={ArrowLeft} onClick={handleBack} aria-label="Volver al listado" />
          <h1 className={styles.resourceActivityFormPage__title}>
            {isCreateMode ? 'Crear actividad' : 'Editar actividad'}
          </h1>
          <div className={styles.resourceActivityFormPage__headerActions}>
            <IconButton
              icon={Save}
              variant="primary"
              onClick={() => (document.getElementById('activity-edit-form') as HTMLFormElement | null)?.requestSubmit()}
              aria-label="Guardar"
            />
            <IconButton
              icon={isSidebarOpen ? PanelRightClose : PanelRightOpen}
              onClick={() => setIsSidebarOpen((v) => !v)}
              aria-label={isSidebarOpen ? 'Ocultar panel de información' : 'Mostrar panel de información'}
            />
          </div>
        </div>
      </div>

      <div className={styles.resourceActivityFormPage__body} data-sidebar-open={isSidebarOpen}>
        <div className={styles.resourceActivityFormPage__main}>
          <ActivityForm
            mode={isCreateMode ? 'create' : 'edit'}
            activity={activity}
            onSave={handleSave}
            onCancel={handleCancel}
            nextOrder={activities.length + 1}
            layout="editor"
            sidebarPortalTarget={sidebarContainer}
            formId="activity-edit-form"
          />
        </div>
        {isSidebarOpen && (
        <aside className={styles.resourceActivityFormPage__sidebar}>
          <Card className={styles.resourceActivityFormPage__sidebarCard}>
            <CardHeader>
              <CardTitle>Información</CardTitle>
            </CardHeader>
            <CardContent className={styles.resourceActivityFormPage__sidebarContent}>
              <div ref={(el) => setSidebarContainer(el)} />
            </CardContent>
          </Card>
        </aside>
        )}
      </div>
    </div>
  );
}

export default ConversationActivityFormPage;
